import classNames from "classnames";
import { styled } from "@linaria/react";
import React, { useRef } from "react";
import { easingBounce, easingBounceMs, radius, shadow, zIndex } from "..";
import useControllableState from "../lib/hooks/useControllableState";
import useOutsideClick from "../lib/hooks/useOutsideClick";
import OptionList from "../OptionList";
import Toggle from "../Toggle";
import usePrevious from "../lib/hooks/usePrevious";
import { color } from "../../ui";
import { spacings } from "../Theme";

const CLOSING = "closing";
const OPENED = "opened";
const CARET = "caret";
const animationDuration = easingBounceMs;

interface P extends React.HtmlHTMLAttributes<HTMLSpanElement> {
  overlay: React.ReactNode | null;
  // controlled
  opened?: boolean;
  setOpened?: React.Dispatch<React.SetStateAction<boolean>>;
  // uncontrolled
  defaultOpened?: boolean;
  // other
  anchor?: "left" | "right";
  padding?: number;
  caret?: boolean;
  children: React.ReactElement;
}

const Wrapper = styled.span`
  position: relative;
  white-space: nowrap;

  & > a {
    display: inline-block;
    border-radius: ${radius};
    padding: 0.4em;
    :hover {
      background-color: ${color.titanium100};
    }
  }

  will-change: z-index;
  &.${OPENED}, &.${CLOSING} {
    z-index: ${zIndex.inlinePopper};
  }

  &.${CARET} > *:last-child::after {
    margin-left: 5px;
    display: inline-block;
    content: "";
    width: 0.55em;
    height: 0.55em;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    will-change: transform;
    transition: transform ${animationDuration}ms ${easingBounce};
    transform: rotate(135deg);
    transform-origin: 65% 35%;
    margin-right: 0.3em;
  }

  &.${CARET}.${OPENED} > *:last-child::after {
    transform: rotate(315deg);
  }
`;

const StyledOptionList = styled(OptionList)`
  position: absolute;
  display: flex;
  flex-direction: column;
  border-radius: ${radius};
  box-shadow: ${shadow[3]};
  background-color: ${color.hydrogen};

  will-change: opacity, transform;
  transform-origin: top center;
  padding: ${spacings.s};
  transition:
    transform ${animationDuration}ms ${easingBounce},
    opacity ${animationDuration}ms ${easingBounce};
  &.enter {
    opacity: 0;
    transform: scale(1, 0.3);
  }
  &.enter-active {
    opacity: 1;
    transform: none;
  }
  &.exit {
    opacity: 1;
    transform: none;
  }
  &.exit-active {
    opacity: 0;
    transform: scale(1, 0.3);
  }
`;

const positionRelativeStyle = { position: "relative" };

const InlineMenu = ({
  children,
  overlay,
  className,
  anchor = "right",
  setOpened: setOpenedProp,
  opened: openedProp,
  defaultOpened = false,
  padding = 8,
  caret = false,
  ...props
}: P) => {
  const [opened, setOpened] = useControllableState(openedProp, setOpenedProp, defaultOpened);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const childrenRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const handleClose = React.useCallback(() => setOpened(false), [setOpened]);
  const handleToggle = React.useCallback(() => setOpened(opened => !opened), [setOpened]);
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ([" ", "ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        setOpened(true);
      }
    },
    [setOpened],
  );
  useOutsideClick(wrapperRef, [wrapperRef], handleClose);

  React.useLayoutEffect(() => {
    if (!opened) return;
    const children = childrenRef.current;
    const menu = menuRef.current;
    if (!children || !menu) throw new Error("Missing ref");
    // read
    window.setTimeout(() => {
      const offsetWidth = children.offsetWidth;
      const offsetHeight = children.offsetHeight;
      // write
      window.requestAnimationFrame(() => {
        menu.style.minWidth = `${offsetWidth + 2 * padding}px`;
        menu.style.top = `-${padding}px`;
        menu.style.paddingTop = `${offsetHeight + 2 * padding}px`;
        if (anchor === "left") {
          menu.style.left = `-${padding}px`;
          menu.style.right = `initial`;
        } else {
          menu.style.left = `initial`;
          menu.style.right = `-${padding}px`;
        }
      });
    });
  }, [children, anchor, padding, opened]);

  // closing
  const previousOpened = usePrevious(opened);
  const closingTimeout = useRef<number>();
  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) throw new Error("Missing wrapperRef?");
    if (!opened && previousOpened) {
      window.requestAnimationFrame(() => {
        wrapper.classList.add(CLOSING);
      });

      closingTimeout.current = window.setTimeout(() => {
        wrapper.classList.remove(CLOSING);
      }, animationDuration);
    }

    return () => {
      if (closingTimeout.current) {
        window.clearTimeout(closingTimeout.current);
      }
    };
  }, [opened, previousOpened]);

  return (
    <Wrapper
      className={classNames(className, { [OPENED]: opened, [CARET]: caret })}
      {...props}
      ref={wrapperRef}
    >
      <Toggle timeout={animationDuration} active={opened}>
        <StyledOptionList ref={menuRef} onRequestClose={handleClose} opened={opened}>
          {overlay}
        </StyledOptionList>
      </Toggle>
      {React.cloneElement(children, {
        onClick: handleToggle,
        onKeyDown: handleKeyDown,
        ref: childrenRef,
        style: positionRelativeStyle,
        className: classNames(children.props.className, {
          [OPENED]: opened,
        }),
      })}
    </Wrapper>
  );
};

export default React.memo(InlineMenu);
